/**
 * FO-406 (batch-51) - facturatiemethode-registry (strategie).
 *
 * Eén definitie per `FacturatieVia`-waarde: label, uitleg, capabilities (wie
 * maakt de factuur, wie accordeert, welke kanalen) en de flow-hooks die de
 * verzendkern + de invoices-routes aansturen. Vóór deze registry zat die kennis
 * verspreid in if-statements door `verzend-factuur.ts`, `invoice-pdf.ts`,
 * `verzend-werkbon-finqle.ts` en de relatie-UI; een nieuwe methode is nu één
 * registry-entry plus de bijbehorende hooks.
 *
 * GEDRAGSNEUTRAAL voor de drie bestaande routes: de teksten en de flow-vlaggen
 * hieronder zijn 1-op-1 overgenomen uit de bestaande code (inclusief de exacte
 * meldingen die de API teruggeeft). FINQLE_API is de nieuwe, vierde methode.
 *
 * Pure module: geen Prisma-, Fastify- of I/O-afhankelijkheid, zodat zowel de API
 * als de backoffice-UI er direct uit kunnen lezen (@streep/types blijft
 * framework-vrij; de waarden spiegelen de Prisma-enum `FacturatieVia`).
 */

/**
 * De facturatiemethodes, in de volgorde waarin ze aan de gebruiker getoond
 * worden. Spiegelt de Prisma-enum `FacturatieVia`; de drift-guard-test
 * (`facturatie-methodes.test.ts`) bewaakt dat beide gelijk blijven.
 */
export const FACTURATIE_VIA_WAARDEN = ['FINQLE', 'FINQLE_API', 'EIGEN', 'HYBRIDE'] as const

export type FacturatieViaKey = (typeof FACTURATIE_VIA_WAARDEN)[number]

/**
 * Wat doet de knop "Factuur verzenden" op een factuur van deze klant?
 *   - EIGEN      → factuur-PDF naar de klant + UBL-XML naar Zenvoices
 *   - FINQLE_PDF → samengestelde PDF op Finqle-briefpapier naar het
 *                  Finqle-parse-adres van de BV (HYBRIDE)
 *   - GEEN       → deze route verstuurt geen facturen vanuit STREEP; zie
 *                  `verzendGeblokkeerdReden`
 */
export type FactuurVerzendKanaal = 'EIGEN' | 'FINQLE_PDF' | 'GEEN'

/** Wie maakt het fiscale factuurdocument. */
export type FactuurMaker = 'STREEP' | 'FINQLE'

/** Waar accordeert de klant (indien van toepassing). */
export type AccorderingDoor = 'GEEN' | 'KLANT_IN_STREEP' | 'KLANT_BIJ_FINQLE'

export interface FacturatieMethodeCapabilities {
  /** Wie maakt het fiscale factuurdocument. */
  factuurMaker: FactuurMaker
  /** Wie accordeert vóór facturatie, en waar. */
  accordering: AccorderingDoor
  /** Kanalen die deze methode gebruikt (leesbare opsomming voor de UI). */
  kanalen: readonly string[]
  /**
   * Maakt de app zelf (concept)facturen voor deze klant? `false` betekent dat
   * de app de Finqle-administratie spiegelt en een eigen-factuur-poging
   * blokkeert (zie `eigenFactuurGeblokkeerdReden`).
   */
  appMaaktEigenFactuur: boolean
  /** Gaat de werkbon per mail naar het Finqle-parse-adres van de BV? */
  werkbonNaarFinqleParseAdres: boolean
  /**
   * Registreert de werkbon-naar-Finqle-forward een facturatieverzoek (bedrag,
   * werkbonnen, verzendmoment)? batch-63 (FO-470): vroeger een interne
   * proforma-factuur; die bestaat niet meer - de naam van de vlag is historisch.
   */
  interneProformaBijWerkbon: boolean
  /**
   * Toont de factuur-PDF de factoring-/cessietekst op Finqle-briefpapier
   * (zonder STREEP-IBAN), mits de BV factoring aan heeft staan?
   */
  factoringOpFactuur: boolean
  /** Spiegelt de app de Finqle-billing-requests via de merchant-API (F1)? */
  spiegeltFinqleApi: boolean
}

export interface FacturatieMethodeFlow {
  /** Welke route neemt "Factuur verzenden" voor deze klant. */
  factuurVerzendKanaal: FactuurVerzendKanaal
  /**
   * Melding wanneer `factuurVerzendKanaal === 'GEEN'`; `null` voor de routes die
   * wél zelf verzenden. Dit is exact de tekst die de API teruggeeft bij
   * `ROUTE_NIET_ONDERSTEUND`.
   */
  verzendGeblokkeerdReden: string | null
  /**
   * Melding wanneer de app voor deze klant geen eigen factuur mag opmaken;
   * `null` wanneer dat wel mag. Gebruikt door de factuur-aanmaakroutes.
   */
  eigenFactuurGeblokkeerdReden: string | null
}

export interface FacturatieMethodeDefinitie {
  key: FacturatieViaKey
  /** Korte naam in lijsten en selecties. */
  label: string
  /** Eén zin voor de kaart-keuze in de relatie-instellingen. */
  korteUitleg: string
  /** Volledige uitleg (bevestigingsdialoog, hulp-tekst). */
  uitleg: string
  capabilities: FacturatieMethodeCapabilities
  flow: FacturatieMethodeFlow
}

/**
 * De melding die de EIGEN-verzendknop teruggeeft voor de factoring-routes.
 * Bestaande tekst uit `verzend-factuur.ts` (ongewijzigd overgenomen).
 */
const FINQLE_VERZEND_GEBLOKKEERD =
  'Alleen eigen-facturatie wordt via deze knop verzonden; Finqle loopt via de werkbon-naar-Finqle-flow.'

/**
 * FO-406 §6.1 - een klant op de Finqle-API-methode krijgt zijn facturen van
 * Finqle; STREEP maakt er geen eigen op. De melding legt uit wat de gebruiker
 * kan doen (methode aanpassen) in plaats van alleen te weigeren.
 */
const FINQLE_API_EIGEN_FACTUUR_GEBLOKKEERD =
  'Deze klant factureert via de Finqle API; Finqle maakt het facturatieverzoek en de factuur op. Pas de facturatiemethode van de klant aan als STREEP zelf moet factureren.'

export const FACTURATIE_METHODES: Record<FacturatieViaKey, FacturatieMethodeDefinitie> = {
  FINQLE: {
    key: 'FINQLE',
    label: 'Finqle (factoring per mail)',
    korteUitleg:
      'De werkbon gaat naar het Finqle-parse-adres van de BV; Finqle factureert en levert de boekhoud-XML zelf aan.',
    uitleg:
      'STREEP stuurt de goedgekeurde werkbon per mail naar het Finqle-parse-adres van de BV en legt dat vast als facturatieverzoek (bedrag, werkbonnen, verzendmoment). Finqle maakt de fiscale factuur op en verstuurt die naar de klant; zodra die factuur via de mailbox of een upload binnenkomt, wordt hij aan het verzoek gekoppeld. STREEP verstuurt zelf geen facturen voor deze klant.',
    capabilities: {
      factuurMaker: 'FINQLE',
      accordering: 'GEEN',
      kanalen: ['Werkbon per mail naar Finqle', 'Facturatieverzoek'],
      appMaaktEigenFactuur: true,
      werkbonNaarFinqleParseAdres: true,
      interneProformaBijWerkbon: true,
      factoringOpFactuur: true,
      spiegeltFinqleApi: false,
    },
    flow: {
      factuurVerzendKanaal: 'GEEN',
      verzendGeblokkeerdReden: FINQLE_VERZEND_GEBLOKKEERD,
      eigenFactuurGeblokkeerdReden: null,
    },
  },
  FINQLE_API: {
    key: 'FINQLE_API',
    label: 'Finqle API (facturatieverzoeken)',
    korteUitleg:
      'Finqle maakt het facturatieverzoek op, de klant accordeert bij Finqle en STREEP toont alle statussen live.',
    uitleg:
      'De werkbon gaat zoals altijd naar het Finqle-parse-adres van de BV. Finqle maakt daar het facturatieverzoek van, stuurt het met proforma en werkbonnen namens STREEP naar de klant en de klant accordeert bij Finqle. Pas daarna ontstaat de definitieve factuur. STREEP maakt voor deze klant geen eigen factuur: de app spiegelt de verzoeken, statussen en facturen via de Finqle-API en toont ze in de facturen-werkruimte.',
    capabilities: {
      factuurMaker: 'FINQLE',
      accordering: 'KLANT_BIJ_FINQLE',
      kanalen: [
        'Werkbon per mail naar Finqle',
        'Facturatieverzoeken via de Finqle-API',
        'Factuur en betaalstatus via API en webhooks',
      ],
      appMaaktEigenFactuur: false,
      werkbonNaarFinqleParseAdres: true,
      interneProformaBijWerkbon: false,
      factoringOpFactuur: true,
      spiegeltFinqleApi: true,
    },
    flow: {
      factuurVerzendKanaal: 'GEEN',
      verzendGeblokkeerdReden:
        'Deze klant factureert via de Finqle API; Finqle verstuurt het facturatieverzoek en de factuur zelf.',
      eigenFactuurGeblokkeerdReden: FINQLE_API_EIGEN_FACTUUR_GEBLOKKEERD,
    },
  },
  EIGEN: {
    key: 'EIGEN',
    label: 'Eigen facturatie',
    korteUitleg:
      'STREEP factureert zelf: de factuur-PDF gaat naar de klant en de UBL-XML naar Zenvoices.',
    uitleg:
      'STREEP maakt de factuur op en kent het definitieve factuurnummer toe. De factuur-PDF gaat naar de aangevinkte factuur-contactpersonen van de klant, de UBL-XML gaat naar het Zenvoices-adres van de BV. Er komt geen Finqle aan te pas.',
    capabilities: {
      factuurMaker: 'STREEP',
      accordering: 'GEEN',
      kanalen: ['Factuur-PDF naar de klant', 'UBL-XML naar Zenvoices'],
      appMaaktEigenFactuur: true,
      werkbonNaarFinqleParseAdres: false,
      interneProformaBijWerkbon: false,
      factoringOpFactuur: false,
      spiegeltFinqleApi: false,
    },
    flow: {
      factuurVerzendKanaal: 'EIGEN',
      verzendGeblokkeerdReden: null,
      eigenFactuurGeblokkeerdReden: null,
    },
  },
  HYBRIDE: {
    key: 'HYBRIDE',
    label: 'Hybride (concept naar Finqle)',
    korteUitleg:
      'STREEP maakt een conceptfactuur en stuurt die naar Finqle; Finqle kent het fiscale nummer toe en factureert.',
    uitleg:
      'STREEP stelt de conceptfactuur samen (inclusief werkbonnen en klantakkoord) en stuurt die als PDF op Finqle-briefpapier naar het Finqle-parse-adres van de BV. Finqle kent het fiscale factuurnummer toe, factureert de klant en levert zijn XML buiten STREEP aan Zenvoices.',
    capabilities: {
      factuurMaker: 'FINQLE',
      accordering: 'KLANT_IN_STREEP',
      kanalen: ['Conceptfactuur-PDF naar Finqle'],
      appMaaktEigenFactuur: true,
      werkbonNaarFinqleParseAdres: false,
      interneProformaBijWerkbon: false,
      factoringOpFactuur: true,
      spiegeltFinqleApi: false,
    },
    flow: {
      factuurVerzendKanaal: 'FINQLE_PDF',
      verzendGeblokkeerdReden: null,
      eigenFactuurGeblokkeerdReden: null,
    },
  },
}

/**
 * De facturatiemethode-definitie voor een `FacturatieVia`-waarde. Losse helper
 * zodat call-sites geen index-signature-narrowing hoeven te doen.
 */
export function getFacturatieMethode(via: FacturatieViaKey): FacturatieMethodeDefinitie {
  return FACTURATIE_METHODES[via]
}

/** Alle methodes in toon-volgorde (kaart-keuze, bulk-omzet, filters). */
export const FACTURATIE_METHODE_LIJST: readonly FacturatieMethodeDefinitie[] =
  FACTURATIE_VIA_WAARDEN.map((k) => FACTURATIE_METHODES[k])

/** Korte labels per methode (lijsten, badges, kolommen). */
export const FACTURATIE_VIA_LABELS: Record<FacturatieViaKey, string> = {
  FINQLE: FACTURATIE_METHODES.FINQLE.label,
  FINQLE_API: FACTURATIE_METHODES.FINQLE_API.label,
  EIGEN: FACTURATIE_METHODES.EIGEN.label,
  HYBRIDE: FACTURATIE_METHODES.HYBRIDE.label,
}

/**
 * De fallback wanneer er (nog) geen systeem-default is ingesteld. Bewust FINQLE:
 * dat is de historische default van `resolveFacturatieVia` en het omzetten naar
 * FINQLE_API is een bewuste beheer-actie ná de sandbox-verificatie (FO-406 §9),
 * niet iets dat een deploy stil doet.
 */
export const FACTURATIE_SYSTEEM_DEFAULT_FALLBACK: FacturatieViaKey = 'FINQLE'

/**
 * Bepaalt de facturatiemethode van een klant: per-BV-override (alleen wanneer
 * het vinkje `facturatieViaPerEntiteit` aan staat én de caller een override voor
 * de facturerende BV aanlevert), anders de expliciete klant-instelling, anders
 * de systeem-default. Pure functie - zowel de systeem-default als de eventuele
 * per-BV-override worden door de caller aangeleverd (API leest ze uit de
 * instellingen/override-tabel, UI uit de server-props), zodat er nergens een
 * verborgen tweede default ontstaat.
 */
export function resolveFacturatieMethode(
  client: { facturatieVia: FacturatieViaKey | null; facturatieViaPerEntiteit?: boolean },
  systeemDefault: FacturatieViaKey = FACTURATIE_SYSTEEM_DEFAULT_FALLBACK,
  /** De per-BV-methode voor de facturerende BV, of null/undefined = geen rij. */
  entiteitOverride?: FacturatieViaKey | null,
): FacturatieViaKey {
  if (client.facturatieViaPerEntiteit === true && entiteitOverride != null) {
    return entiteitOverride
  }
  return client.facturatieVia ?? systeemDefault
}
